import {
  EditFilled,
  ExportOutlined,
  InfoCircleOutlined,
  LockFilled,
} from '@ant-design/icons';
import { type BrandedHref } from '@framework/ui/core/router/TypedRoute.tsx';
import { Button, Flex, Tooltip, Typography } from 'antd';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const SharedSectionContainer = styled.div`
  border: 1px solid ${props => props.theme.colorWarning};
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 24px;
`;

const SharedSectionHeader = styled(Flex)`
  background-color: ${props => props.theme.colorWarningBg};
  min-height: 43px;
  padding: 4px 16px;
  color: ${props => props.theme.colorText};
  border-bottom: 1px solid ${props => props.theme.colorWarning};
`;

const SharedSectionContent = styled.div`
  padding: 0 16px;
  padding-top: 24px;
`;

export interface ISharedSectionWrapperProps {
  children: React.ReactNode;
  /** Modalità del wrapper: "editable" per sezioni di proprietà, "readonly" per sezioni esterne */
  mode: 'editable' | 'readonly';
  /** Link al modulo di origine della sezione condivisa (href assoluto col basename) */
  sourceHref?: BrandedHref | null;
}

/**
 * Wrapper grafico per le sezioni condivise.
 *
 * - **editable**: sezione di proprietà del modulo corrente, condivisa con altri moduli.
 *   Mostra un avviso che modificare i dati impatterà gli altri moduli.
 * - **readonly**: sezione proveniente da un altro modulo, in sola lettura.
 *   Mostra il lucchetto e il pulsante per navigare al modulo di origine.
 */
export function SharedSectionWrapper(props: ISharedSectionWrapperProps) {
  const { children, mode, sourceHref } = props;
  const { t } = useTranslation();

  return (
    <SharedSectionContainer>
      <SharedSectionHeader align="center" justify="space-between" wrap>
        <Flex align="center" gap={8}>
          {mode === 'readonly' ? (
            <LockFilled aria-hidden="true" />
          ) : (
            <EditFilled aria-hidden="true" />
          )}
          <Typography.Text strong>
            {mode === 'readonly'
              ? t(
                  'shared-section.readonly-label',
                  'SEZIONE DATI CONDIVISA — SOLA LETTURA',
                )
              : t('shared-section.editable-label', 'SEZIONE DATI CONDIVISA')}
          </Typography.Text>
          <Tooltip
            title={
              mode === 'readonly'
                ? t(
                    'shared-section.readonly-tooltip',
                    'Questa sezione proviene da un altro modulo e non può essere modificata qui.',
                  )
                : t(
                    'shared-section.editable-tooltip',
                    'La sezione è condivisa con altri moduli della domanda. Modificando i dati di questa sezione modificherai anche i dati degli altri moduli, invalidandoli qualora siano già conclusi o firmati.',
                  )
            }
          >
            <InfoCircleOutlined
              aria-label={t(
                'shared-section.info-tooltip',
                'Informazioni sulla sezione condivisa',
              )}
            />
          </Tooltip>
        </Flex>

        {mode === 'readonly' && sourceHref && (
          <Button
            type="default"
            href={sourceHref}
            icon={<ExportOutlined aria-hidden="true" />}
            iconPosition="end"
          >
            {t(
              'shared-section.navigate-to-source',
              'Modifica nel modulo di origine',
            )}
          </Button>
        )}
      </SharedSectionHeader>
      <SharedSectionContent>{children}</SharedSectionContent>
    </SharedSectionContainer>
  );
}
